// W37 / M37 — Nyitóoldal.
//
// Ez az egyetlen képernyő, amit belépés nélkül is bárki megnyit (és a keresők
// indexelnek), ezért nem az alkalmazás-héjban él: saját fejléce és lábléce van,
// a szerkezete pedig szigorúan szemantikus — egyetlen h1, valódi nav, section
// elemek horgonyokkal. A megjelenést végig a ds.css osztályai adják.

import { t } from '../lib/i18n';
import { sutiBeallitasLink } from './cookie-banner';
import { h, icon, icons } from '../lib/dom';
import { nyelvValaszto } from '../lib/language-switch';
import { metaEsemeny } from '../lib/meta-pixel';

/// A tervekből átemelt ikon-útvonalak.
///
/// Szándékosan itt maradnak, és nem kerülnek a közös `icons` készletbe: csak a
/// nyitóoldal funkció-kártyáin használjuk őket.
const featurePaths = {
  /// Pipás négyzet — kiosztás és elfogadás.
  accept:
    'M4.6 2.6h6.8a2 2 0 012 2v6.8a2 2 0 01-2 2H4.6a2 2 0 01-2-2V4.6a2 2 0 012-2z' +
    'M5.4 8.2l1.8 1.8 3.4-3.6',
  /// Stopperóra — időmérés.
  timer: 'M8 4.2a4.8 4.8 0 100 9.6 4.8 4.8 0 000-9.6M8 6.6V9l1.6 1M6.2 2.2h3.6',
  /// Kézjegy — helyszíni aláírás.
  signature: 'M2.4 11.6c2.6 0 2.6-7 5-7s2 7 4.4 7c.9 0 1.5-.5 1.8-1M2.4 13.8h11.2',
  /// Áthúzott térerő — offline működés.
  offline:
    'M2 2l12 12M5.2 9.6a4 4 0 014.4-.8M2.6 6.8a8 8 0 013.2-1.9' +
    'M13.4 6.8a8 8 0 00-3.6-2M8 12.4v.2',
} as const;

// FONTOS: az alabbi tablak MODUL SZINTU konstansok, vagyis a fajl
// betoltesekor allnak ossze — a szotar viszont csak a render elott erkezik
// meg. Ezert SZOVEG HELYETT KULCSOT tarolnak, es a forditas a rendereleskor
// tortenik. Kesz szoveggel a nyitooldal a nyers kulcsokkal fagyna be.

type Feature = { titleKey: string; textKey: string; path: string };

const features: Feature[] = [
  { titleKey: 'landing.acceptTitle', textKey: 'landing.acceptText', path: featurePaths.accept },
  { titleKey: 'landing.timerTitle', textKey: 'landing.timerText', path: featurePaths.timer },
  {
    titleKey: 'landing.signatureTitle',
    textKey: 'landing.signatureText',
    path: featurePaths.signature,
  },
  { titleKey: 'landing.offlineTitle', textKey: 'landing.offlineText', path: featurePaths.offline },
];

type NavLink = { id: string; labelKey: string };

const navLinks: NavLink[] = [
  { id: 'funkciok', labelKey: 'landing.navFeatures' },
  { id: 'kinek-jo', labelKey: 'landing.navAudience' },
  { id: 'arak', labelKey: 'landing.navPricing' },
  { id: 'kapcsolat', labelKey: 'landing.navContact' },
];

/// Az árszakasz csomagjai.
///
/// Az árazás FEJENKÉNTI, mert a Stripe is így számláz: két termék van
/// (`BlazeData — Munkahely` és `Munkahely + AI`), mindkettő havi, licenc
/// alapú ár, és a darabszámot a cég veszi meg. A korábbi „5 felhasználóig /
/// 15 felhasználóig" csomagolás ezzel ellentmondott volna: a nyitóoldal fix
/// havidíjat ígért, a számla viszont fejenként érkezett volna.
///
/// A három kártya megmaradt, de már NEM három árszint: kettő a két valódi
/// termék, a harmadik az egyedi ajánlat. A létszámsávok ajánlássá váltak.
const plans: {
  nameKey: string;
  audienceKey: string;
  priceKey: string;
  /// Az ár mértékegysége („/ hó / fő"). Az egyedi ajánlatnál nincs.
  unitKey?: string;
  detailKey: string;
  highlight?: boolean;
}[] = [
  {
    nameKey: 'landing.planStartName',
    audienceKey: 'landing.planStartAudience',
    priceKey: 'landing.planBasicPrice',
    unitKey: 'landing.planUnit',
    detailKey: 'landing.planStartDetail',
  },
  {
    nameKey: 'landing.planTeamName',
    audienceKey: 'landing.planTeamAudience',
    priceKey: 'landing.planAiPrice',
    unitKey: 'landing.planUnit',
    detailKey: 'landing.planTeamDetail',
    highlight: true,
  },
  {
    nameKey: 'landing.planCustomName',
    audienceKey: 'landing.planCustomAudience',
    priceKey: 'landing.planCustomPrice',
    detailKey: 'landing.planCustomDetail',
  },
];

// HAT SZAKMA, nem harom. Harommal a szakasz egyetlen sor volt, es a latogato
// nagy esellyel nem talalta kozte a sajatjat — pedig a kerdes pont az, hogy
// „ez nekem valo-e". A `c3` racs hat elemmel ket sort ad, es a lista igy
// lefedi a tipikus szakipari koroket.
const audiences: { titleKey: string; textKey: string; iconPath: string }[] = [
  { titleKey: 'landing.audienceMechTitle', textKey: 'landing.audienceMechText', iconPath: icons.timer },
  { titleKey: 'landing.audienceHoaTitle', textKey: 'landing.audienceHoaText', iconPath: icons.calendar },
  { titleKey: 'landing.audienceOpsTitle', textKey: 'landing.audienceOpsText', iconPath: icons.chart },
  { titleKey: 'landing.audienceBuildTitle', textKey: 'landing.audienceBuildText', iconPath: icons.folder },
  { titleKey: 'landing.audienceServiceTitle', textKey: 'landing.audienceServiceText', iconPath: icons.check },
  { titleKey: 'landing.audienceCleanTitle', textKey: 'landing.audienceCleanText', iconPath: icons.users },
];

function brandMark(size = 15): HTMLElement {
  return h('span', { class: 'mk' }, h('span', { class: 'ic' }, icon(icons.spark, size)));
}

function featureCard(feature: Feature): HTMLElement {
  return h(
    'article',
    { class: 'card pad20 col g12 landing-feature' },
    h(
      'span',
      {
        class: 'ic',
        style: {
          width: '38px',
          height: '38px',
          borderRadius: 'var(--r-md)',
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
        },
      },
      icon(feature.path, 19),
    ),
    h('h3', { class: 'h3', text: t(feature.titleKey) }),
    h('p', { class: 'body-sm ts', text: t(feature.textKey) }),
  );
}

/// A szakaszok közös kerete: azonos margó, és a hosszú magyar mondatok
/// olvasható sorhosszon maradnak.
function section(
  id: string,
  attrs: { surface?: boolean },
  ...children: (HTMLElement | null)[]
): HTMLElement {
  // OSZTALY es nem beagyazott stilus: a beagyazott `padding` erosebb minden
  // media lekerdezesnel, tehat keskeny kepernyon nem lehetne szukiteni.
  return h(
    'section',
    { id, class: `col g24 landing-section${attrs.surface ? ' on-surface' : ''}` },
    ...children,
  );
}

function sectionHead(label: string, title: string, lead?: string): HTMLElement {
  return h(
    'div',
    { class: 'col g6', style: { maxWidth: '640px' } },
    h('span', { class: 'label', text: label }),
    h('h2', { class: 'h1', text: title }),
    lead ? h('p', { class: 'body ts', text: lead }) : null,
  );
}

export function landingPage(options: {
  onLogin: () => void;
  onRegister?: () => void;
}): HTMLElement {
  function loginButton(cls: string, label = t('auth.loginTitle')): HTMLElement {
    return h('button', { class: cls, type: 'button', text: label, onClick: options.onLogin });
  }

  const header = h(
    'header',
    { class: 'topbar landing-topbar' },
    h('span', { class: 'logo', style: { fontSize: '17px' } }, brandMark(), 'Blaze'),
    h(
      'nav',
      {
        class: 'row g24 landing-nav',
        'aria-label': t('landing.navLabel'),
        style: { marginLeft: '16px' },
      },
      ...navLinks.map((link) =>
        h('a', {
          class: 'body-sm w6',
          href: `#${link.id}`,
          text: t(link.labelKey),
          style: { color: 'var(--ts)', textDecoration: 'none' },
        }),
      ),
    ),
    h('span', { class: 'grow' }),
    // A NYELVVALASZTO A FEJLECBEN, rovid (HU / EN) alakban. Eloszor a
    // lablecben volt, de ott a latogato nem talalja meg: a nyelv valasztasa
    // az elso masodpercek dontese, nem az, amiert a lap aljara gorgetunk.
    //
    // A rovid alak nem szepseg kerdese: 375 px-en a fejlec eddig is pontosan
    // kitoltotte a szelesseget a logoval es a ket gombbal. A `.nyelv-valaszto`
    // ezert szuk kijelzon kisebb betut es behuzast kap (`app.css`).
    nyelvValaszto({ rovid: true }),
    // Regisztracio nelkul a Belepes az elsodleges gomb; ha van regisztracio,
    // az lesz az elsodleges ut, a belepes pedig masodlagos.
    ...(options.onRegister
      ? [
          loginButton('btn'),
          h('button', {
            class: 'btn btn-primary',
            type: 'button',
            text: t('register.title'),
            onClick: options.onRegister,
          }),
        ]
      : [loginButton('btn btn-primary')]),
  );

  /// A hős-szakasz jobb oldali ábrája: a TERMÉK SAJÁT felülete.
  ///
  /// Szándékosan nem rajzolt illusztráció, hanem ugyanazok a `ds.css`
  /// elemek, amikből az app épül (`panel dense`, `thead`, `lrow`, státusz- és
  /// prioritás-jelvények). Így amit a látogató itt lát, azt kapja belépés
  /// után is — egy kitalált, szebb ábra ígéretet tenne a termék helyett.
  ///
  /// A sorok tartalma nyilvánvalóan minta; valódi ügyféladat nem kerülhet
  /// nyilvános oldalra.
  function heroMock(): HTMLElement {
    const sor = (
      azonosito: string,
      cim: string,
      projekt: string,
      prio: string,
      prioCls: string,
      allapot: string,
      allapotCls: string,
    ) =>
      h(
        'div',
        { class: 'lrow', style: { flexWrap: 'nowrap', gap: '10px' } },
        h('span', { class: 'mono caption', style: { width: '34px', flex: 'none' }, text: azonosito }),
        h(
          'span',
          { class: 'col grow', style: { gap: '1px', minWidth: '0' } },
          h('span', {
            class: 'body-sm w6',
            style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
            text: cim,
          }),
          h('span', { class: 'caption', text: projekt }),
        ),
        h('span', { class: `prio ${prioCls}`, style: { flex: 'none' }, text: prio }),
        h('span', { class: `badge ${allapotCls}`, style: { flex: 'none' }, text: allapot }),
      );

    return h(
      'div',
      {
        class: 'panel dense',
        'aria-hidden': 'true',
        style: {
          overflow: 'hidden',
          boxShadow: '0 18px 48px rgba(0,0,0,.28)',
          transform: 'rotate(-.4deg)',
        },
      },
      h(
        'div',
        { class: 'thead', style: { flexWrap: 'nowrap' } },
        h('span', { class: 'th grow', text: t('report.colTask') }),
        h('span', { class: 'th', style: { width: '92px', flex: 'none' }, text: t('task.priority') }),
        h('span', { class: 'th', style: { width: '112px', flex: 'none' }, text: t('task.status') }),
      ),
      sor('#1', t('landing.mockTask1'), t('landing.mockProject1'), t('task.highPriority'), 'p-high', t('task.inProgress'), 'st-prog'),
      sor('#2', t('landing.mockTask2'), t('landing.mockProject1'), t('task.midPriority'), 'p-med', t('task.waitingToAccept'), 'st-wait'),
      sor('#3', t('landing.mockTask3'), t('landing.mockProject2'), t('task.lowPriority'), 'p-low', t('task.successfulFulfillment'), 'st-ok'),
    );
  }

  const hero = h(
    'section',
    {
      id: 'fooldal',
      class: 'landing-hero',
    },
    h(
      'div',
      { class: 'landing-hero-inner' },
      h(
        'div',
        { class: 'col g16', style: { maxWidth: '620px', minWidth: '0' } },
        h('span', { class: 'badge st-prog', style: { alignSelf: 'flex-start' }, text: t('landing.eyebrow') }),
        h('h1', { class: 'display landing-headline', text: t('landing.headline') }),
        h('p', { class: 'body-lg ts', text: t('landing.heroLead') }),
        h(
          'div',
          { class: 'row g12 wrap', style: { marginTop: '4px' } },
          loginButton('btn btn-primary btn-lg'),
          h('a', { class: 'btn btn-secondary btn-lg', href: '#funkciok', text: t('landing.heroCta') }),
        ),
        h('span', { class: 'caption', text: t('landing.assurance') }),
      ),
      h('div', { class: 'landing-hero-art' }, heroMock()),
    ),
  );

  const featureSection = section(
    'funkciok',
    {},
    sectionHead(t('landing.navFeatures'), t('landing.featuresTitle')),
    // Két oszlop: a hosszabb magyar leírások így is olvasható sorhosszon férnek el.
    h('div', { class: 'grid c2' }, ...features.map(featureCard)),
  );

  const audienceSection = section(
    'kinek-jo',
    { surface: true },
    sectionHead(t('landing.navAudience'), t('landing.audienceTitle')),
    h(
      'div',
      { class: 'grid c3' },
      ...audiences.map((item) =>
        h(
          'div',
          {
            class: 'col g8',
            style: { padding: '4px 0 4px 20px', borderLeft: '3px solid var(--accent)' },
          },
          h(
            'div',
            { class: 'row g8', style: { alignItems: 'center' } },
            h(
              'span',
              {
                class: 'ic',
                style: {
                  width: '28px',
                  height: '28px',
                  borderRadius: 'var(--r-sm)',
                  background: 'var(--accent-soft)',
                  color: 'var(--accent)',
                },
              },
              icon(item.iconPath, 15),
            ),
            h('h3', { class: 'h3', text: t(item.titleKey) }),
          ),
          h('p', { class: 'body-sm ts', text: t(item.textKey) }),
        ),
      ),
    ),
  );

  const pricingSection = section(
    'arak',
    {},
    sectionHead(t('landing.navPricing'), t('landing.pricingTitle'), t('landing.pricingLead')),
    h(
      'div',
      { class: 'grid c3 landing-plans' },
      ...plans.map((plan) =>
        h(
          'article',
          {
            class: 'card pad20 col g12 landing-plan',
            style: plan.highlight
              ? { borderColor: 'var(--accent)', background: 'var(--accent-soft)' }
              : undefined,
          },
          plan.highlight
            ? h('span', { class: 'badge st-prog', style: { alignSelf: 'flex-start' }, text: t('landing.planPopular') })
            : null,
          h(
            'div',
            { class: 'col', style: { gap: '2px' } },
            h('h3', { class: 'h3', text: t(plan.nameKey) }),
            h('span', { class: 'caption', text: t(plan.audienceKey) }),
          ),
          h(
            'div',
            { class: 'row', style: { alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' } },
            h('span', { class: 'display', style: { fontSize: '32px' }, text: t(plan.priceKey) }),
            plan.unitKey
              ? h('span', { class: 'body-sm ts', text: t(plan.unitKey) })
              : null,
          ),
          h('span', { class: 'body-sm ts', text: t(plan.detailKey) }),
          // A gomb a kartya ALJARA kerul (`margin-top: auto`), a kartyak pedig
          // egyforma magasak (a racs nyujtja oket). Enelkul a kiemelt kartya
          // a jelvenye miatt magasabb volt, a harmadik pedig rovidebb
          // szoveggel alacsonyabb — a gombok harom kulonbozo magassagban
          // alltak, ami osszehasonlitaskor zavaro.
          h('span', { class: 'grow' }),
          h('button', {
            class: plan.highlight ? 'btn btn-primary' : 'btn btn-secondary',
            type: 'button',
            text: t('landing.planCta'),
            onClick: options.onRegister ?? options.onLogin,
          }),
        ),
      ),
    ),
  );

  const contactSection = h(
    'section',
    {
      id: 'kapcsolat',
      class: 'col g24 landing-section landing-contact',
    },
    h('h2', {
      class: 'display',
      style: { fontSize: '38px', color: 'var(--on-accent)' },
      text: t('landing.contactTitle'),
    }),
    h('p', {
      class: 'body-lg',
      style: { maxWidth: '560px', opacity: '.92' },
      text: t('landing.contactLead'),
    }),
    h(
      'div',
      { class: 'row g12 wrap' },
      // A FO UT MOSTANTOL A KAPCSOLAT OLDAL, nem a `mailto:`. A `mailto:`
      // a latogato levelezoprogramjatol fugg, es ha az nem indul el — mobilon
      // gyakori —, a megkereses nyomtalanul elveszik. Az urlap viszont elobb
      // adatbazisba ir. A levelcim megmarad masodlagos utkent a lap aljan.
      h('a', {
        class: 'btn btn-lg',
        href: '#/kapcsolat',
        style: { background: 'var(--on-accent)', color: 'var(--accent)' },
        text: t('landing.contactWrite'),
      }),
      // Akcentus-háttéren a szokásos gomb-változatok kontrasztja kevés, ezért
      // itt a keret és a szöveg is az akcentuson használható tokent kapja.
      h('a', {
        class: 'btn btn-lg',
        href: '#/kapcsolat?tipus=hiba',
        style: { borderColor: 'var(--on-accent)', color: 'var(--on-accent)' },
        text: t('landing.contactBug'),
      }),
      ...(options.onRegister
        ? [
            h('button', {
              class: 'btn btn-lg',
              type: 'button',
              text: t('register.submit'),
              style: { borderColor: 'var(--on-accent)', color: 'var(--on-accent)' },
              onClick: options.onRegister,
            }),
          ]
        : []),
      h('button', {
        class: 'btn btn-lg',
        type: 'button',
        text: t('landing.contactHaveAccount'),
        style: { borderColor: 'var(--on-accent)', color: 'var(--on-accent)' },
        onClick: options.onLogin,
      }),
    ),
    h(
      'span',
      { class: 'caption', style: { opacity: '.9' } },
      t('landing.contactDirect') + ' ',
      h('a', {
        href: 'mailto:balazs.lang@blazedata.hu',
        style: { color: 'var(--on-accent)', fontWeight: '600' },
        text: 'balazs.lang@blazedata.hu',
        // GYENGÉBB JEL, DE JEL. Az űrlapnál tudjuk, hogy a megkeresés tényleg
        // megérkezett; itt csak annyit, hogy a látogató elindította a
        // levelezőprogramját — hogy a levelet elküldte-e, soha nem derül ki.
        // Ezért `Contact` és nem `Lead`: a hirdetési rendszer így a kettőt
        // külön súlyozza, és a bemutatókérésre az űrlapból tanul.
        onClick: () => {
          metaEsemeny('Contact', { content_name: 'blaze_demo_request_mailto' });
        },
      }),
    ),
  );

  const footer = h(
    'footer',
    {
      class: 'row g16 wrap',
      style: {
        padding: '32px',
        borderTop: '1px solid var(--border)',
        background: 'var(--surface)',
      },
    },
    h('span', { class: 'logo', style: { fontSize: '15px' } }, brandMark(), 'Blaze'),
    h('span', { class: 'caption', text: t('landing.footerCompany') }),
    h('span', { class: 'grow' }),
    h(
      'nav',
      { class: 'row g16', 'aria-label': t('landing.footerLegalLabel') },
      h('a', { class: 'caption', href: '#/adatkezeles', text: t('landing.footerPrivacy') }),
      h('a', { class: 'caption', href: '#/aszf', text: t('landing.footerTerms') }),
      h('a', { class: 'caption', href: '#/kapcsolat', text: t('landing.footerContact') }),
      // Eszkoz nelkul nincs mit beallitani — ilyenkor a link sem jelenik meg.
      ...(sutiBeallitasLink() ? [sutiBeallitasLink()!] : []),
    ),
  );

  return h(
    'div',
    { class: 'col', style: { gap: '0', minHeight: '100%', background: 'var(--bg)' } },
    header,
    h('main', { class: 'col', style: { gap: '0' } }, hero, featureSection, audienceSection, pricingSection, contactSection),
    footer,
  );
}
